import React, { useRef, useState, useEffect, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useAuth } from "utils/useAuth/useAuth";
import { PortalContext } from "App";
import { trimObject, checkNonMoneyDataForTable } from "utils/utils";
import useThrottleCallback from "utils/useThrottleCallback/useThrottleCallback";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import CreateFormContent from "pages/finance/debit-notes/pending-approval/components/CreateFormContent/CreateFormContent";
import s from "./MerchantMdr.module.scss";
import { queryList, createDebitApplication, exportCsv } from "./model";

const MerchantMdr = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const tabContext = useContext(PortalContext);
  const { currentPath } = tabContext;
  const gridId = "MerchantMdrGrid";
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [collapse, setCollapse] = useState(true);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState({
    merchantId: "",
    merchantName: "",
    bankAccount: "",
    aipMerchantNo: "",
  });
  const [focus, setFocus] = useState({});

  const [columnsArray, setColumnsArray] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [oncreateData, setOncreateData] = useState({});
  const [createFormData, setCreateFormData] = useState({
    merchantId: "",
    paymentGateway: "",
    currencyCode: "HKD",
    debitAmount: "",
    reason: "",
  });
  const [openSubTableRow, setOpenSubTableRow] = useState(NaN);

  const windowResizeHandler = useThrottleCallback(() => {
    setWindowWidth(window.innerWidth);
  }, 20);
  useEffect(() => {
    if (currentPath === path) {
      const maxHeight = 76;
      const grid = document.getElementById(gridId);
      if (grid.scrollHeight - 15 <= maxHeight) {
        setCollapse(undefined);
      } else {
        if (collapse === undefined || collapse) {
          setCollapse(true);
        } else {
          setCollapse(false);
        }
      }
    }
    window.addEventListener("resize", windowResizeHandler);
    return () => {
      removeEventListener("resize", windowResizeHandler);
    };
  }, [windowWidth, currentPath]);

  useEffect(() => {
    const init = async () => {
      await initialFetch();
    };
    init();
  }, []);

  const handleQueryListUIResponse = (apiResponse) => {
    if (!apiResponse || !apiResponse?.data) {
      return snackbar(t("general.message.dynamicNetworkError", { data: `${t("page.finance.merchant.mdr.sectionName")}` }), "error");
    }
    const { status, data: responseBody } = apiResponse;

    if (status === 401) {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    }

    if (status === 403) {
      snackbar(t("general.message.unauthorized"), "error");
      return;
    }

    switch (responseBody.responseCode) {
      case "0000":
        if (responseBody?.data?.length === 0) {
          snackbar(t("general.message.no.result"), "warning");
        }
        setDatasource(responseBody);
        setCurrentPage(responseBody.page);
        setPageSize(responseBody.size);
        Object.assign(pageSizeInfoRef.current, {
          currentpage: responseBody.page,
          pagesize: responseBody.size,
        });
        break;

      case "2408":
        snackbar(t("general.message.no.result"), "warning");
        setDatasource({ data: [], page: 0, total: 0 });
        setCurrentPage(0);
        setPageSize(20);
        Object.assign(pageSizeInfoRef.current, {
          currentpage: 0,
          pagesize: 20,
        });
        break;

      default:
        snackbar(t("general.message.networkError"), "error");
        break;
    }
  };

  const initialFetch = async () => {
    setLoading(true);
    const apiResponse = await queryList({
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
    });
    handleQueryListUIResponse(apiResponse);
    setSearch(false);
    setLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput({
      merchantId: "",
      merchantName: "",
      bankAccount: "",
      aipMerchantNo: "",
    });
  };

  const handleSearchFieldCollapse = (status) => {
    setCollapse(status);
  };

  const handleReset = async () => {
    resetInputField();
    await initialFetch();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await queryList(payload);
    handleQueryListUIResponse(apiResponse);
    setSearch(true);
    setSearching(false);
  };

  const handleExport = async () => {
    setExporting(true);
    const apiResponse = await exportCsv({});
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.export.function.success.message"),
              "success",
            );
            const url = responseBody.data;
            window.location = url;
          } else if (responseBody.responseCode === "2200") {
            snackbar(
              t("general.message.export.function.error.message.5"),
              "warning",
            );
          }
        }
      }
    }

    setExporting(false);
  };

  // ===================== create debit funciton ===================== //
  const openCreateDialog = (data) => {
    setCreateFormData({
      merchantId: data.merchantId ? data.merchantId : "",
      currencyCode: "HKD",
    });
    setOncreateData(data);
    setIsCreateDialogOpen(true);
    setDialogLoading(false);
  };

  const closeCreateDialog = () => {
    restoreCreateFormInput();
    setIsCreateDialogOpen(false);
    setDialogLoading(false);
  };

  const handleCreateFormInput = (data, field) => {
    setCreateFormData({
      ...createFormData,
      [field]: data,
    });
  };

  const restoreCreateFormInput = () => {
    setCreateFormData({
      merchantId: "",
      paymentGateway: "",
      currencyCode: "HKD",
      debitAmount: "",
      reason: "",
    });
  };

  const createContent = () => {
    return (
      <CreateFormContent
        handleInput={handleCreateFormInput}
        data={oncreateData}
      />
    );
  };

  const handleCreateDebitApplication = async () => {
    const payload = trimObject({ ...createFormData });
    const apiResponse = await createDebitApplication(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.create.function.success.message"),
              "success",
            );
            return true;
          } else {
            snackbar(
              t("general.message.create.function.fail.message"),
              "error",
            );
            return false;
          }
        }
      }
    }
  };

  const confirmCreate = async () => {
    if (!createFormData.merchantId) {
      snackbar(
        t("page.finance.debit.pending.list.create.function.error.message.1"),
        "warning",
      );
      return;
    }
    if (!createFormData.currencyCode) {
      snackbar(
        t("page.finance.debit.pending.list.create.function.error.message.2"),
        "warning",
      );
      return;
    }
    if (!createFormData.debitAmount) {
      snackbar(
        t("page.finance.debit.pending.list.create.function.error.message.3"),
        "warning",
      );
      return;
    }
    setDialogLoading(true);
    const result = await handleCreateDebitApplication();
    closeCreateDialog();
    if (result) {
      const payload = {
        name: "Pending Approval",
        path: "/finance/debit-notes/pending-approval",
      };
      redirectFn("page", payload);
    }
  };

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field) => {
    setFocus({ [field]: false });
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const handleOpenSubTable = (id) => {
    if (openSubTableRow === id) {
      setOpenSubTableRow(NaN);
    } else {
      setOpenSubTableRow(id);
    }
  };

  //MDR display value validator
  const checkMDRForTable = (data, key) => {
    if (!data || !data[key]) return "0";
    return data[key];
  };

  const subTableColumns = [
    {
      key: "cardType",
      title: t("page.finance.merchant.mdr.subtable.column.title.card.type"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "cost",
      title: t("page.finance.merchant.mdr.subtable.column.title.cost"),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMDRForTable(data, key);
      },
    },
    {
      key: "mdr",
      title: t("page.finance.merchant.mdr.subtable.column.title.mdr"),
      sorter: false,
      render: (data, key) => {
        return checkMDRForTable(data, key);
      },
    },
    {
      key: "foreignMdr",
      title: t("page.finance.merchant.mdr.subtable.column.title.mdr"),
      sorter: false,
      render: (data, key) => {
        return checkMDRForTable(data, key);
      },
    },
    {
      key: "salesCommission",
      title: t(
        "page.finance.merchant.mdr.subtable.column.title.sales.commission",
      ),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMDRForTable(data, key);
      },
    },
    {
      key: "referrerCommission",
      title: t(
        "page.finance.merchant.mdr.subtable.column.title.referrer.commission",
      ),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMDRForTable(data, key);
      },
    },
    {
      key: "remark",
      title: t("page.finance.merchant.mdr.subtable.column.title.remark"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
  ].map((column, index) => ({ ...column, index }));

  const columns = [
    {
      index: 0,
      key: "merchantId",
      title: t("page.finance.merchant.mdr.column.title.merchant.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "aipMerchantNo",
      title: t("page.finance.merchant.mdr.column.title.aip.mid"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "merchantName",
      title: t("page.finance.merchant.mdr.column.title.merchant.name"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 4,
      key: "email",
      title: t("page.finance.merchant.mdr.column.title.email"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 5,
      key: "bankAccount",
      title: t("page.finance.merchant.mdr.column.title.bank.account"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 6,
      key: "beneficiary",
      title: t("page.finance.merchant.mdr.column.title.beneficiary"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 7,
      key: "settleCycle",
      title: t("page.finance.merchant.mdr.column.title.settlement.cycle"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 8,
      key: "zeroPlan",
      title: t("page.finance.merchant.mdr.column.title.zero.plan"),
      sorter: false,
      render: (data) => {
        return data ? "Y" : "N";
      },
    },
    {
      index: 9,
      key: "sales",
      title: t("page.finance.merchant.mdr.column.title.sales"),
      sorter: false,
      render: (data, key) => {
        if (!data[key]) {
          return "-";
        } else {
          return data[key].firstName;
        }
      },
    },
    {
      index: 10,
      key: "referrer",
      title: t("page.finance.merchant.mdr.column.title.referrer"),
      sorter: false,
      render: (data, key) => {
        if (!data[key]) {
          return "-";
        } else {
          return data[key].company;
        }
      },
    },
    {
      index: 11,
      key: "action",
      title: t("page.finance.merchant.mdr.column.title.action"),
      sorter: false,
      render: (data) => {
        return (
          <div className={s.actionBtnContainer}>
            <button onClick={() => openCreateDialog(data)}>
              {t("page.finance.merchant.mdr.column.title.action.1")}
            </button>
          </div>
        );
      },
    },
  ];

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 50, 100],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={collapse}>
          <OutlinedText
            label={t("page.finance.merchant.mdr.merchant.id.label")}
            value={searchFieldInput.merchantId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "merchantId")}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId")}
            sxParams={{ textFocus: focus.merchantId }}
          />
          <OutlinedText
            label={t("page.finance.merchant.mdr.merchant.name.label")}
            value={searchFieldInput.merchantName}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "merchantName")}
            onfocusFn={() => textInputOnFocus("merchantName")}
            onblurFn={() => textInputOnBlur("merchantName")}
            sxParams={{ textFocus: focus.merchantName }}
          />
          <OutlinedText
            label={t("page.finance.merchant.mdr.bank.account.label")}
            value={searchFieldInput.bankAccount}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "bankAccount")}
            onfocusFn={() => textInputOnFocus("bankAccount")}
            onblurFn={() => textInputOnBlur("bankAccount")}
            sxParams={{ textFocus: focus.bankAccount }}
          />
          <OutlinedText
            label={t("page.finance.merchant.mdr.aip.mid.label")}
            value={searchFieldInput.aipMerchantNo}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "aipMerchantNo")}
            onfocusFn={() => textInputOnFocus("aipMerchantNo")}
            onblurFn={() => textInputOnBlur("aipMerchantNo")}
            sxParams={{ textFocus: focus.aipMerchantNo }}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching || exporting}
          collapse={collapse}
          handleCollapse={handleSearchFieldCollapse}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={loading || searching || exporting}
          exporting={exporting}
          exportBtn
          handleExport={handleExport}
          refreshBtn
          handleRefresh={handleSearch}
          columnDisplayBtn
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "finance-merchant-mdr",
          }}
        />
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
              subTable
              subTableDisplayRow={openSubTableRow}
              handleOpenSubTableRow={handleOpenSubTable}
              subTableColumn={subTableColumns}
              subTableContent={"mdrList"}
              subTableSortCustomOrder={[
                "VISA",
                "MASTER",
                "UNIONPAY",
                "JCB",
                "AMEX",
                "WECHAT",
                "ALIPAY",
                "OCTOPUS",
              ]}
            />
          )}

          {searching && <Spin type={"search"} />}
        </div>
        <SimpleDialog
          open={isCreateDialogOpen}
          dialogOncloseFn={closeCreateDialog}
          title={t(
            "page.finance.debit.pending.list.create.function.dialog.title",
          )}
          content={createContent()}
          confirmFn={confirmCreate}
          positiveAction={t("component.simple.dialog.action.3")}
          negativeAction={t("component.simple.dialog.action.4")}
          negativeActionDisabled={dialogLoading}
          positiveActionLoading={dialogLoading}
        />
      </div>
    </div>
  );
};

export default React.memo(MerchantMdr);
